import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FileImage } from "@/components/FileImage";
import { ImageUpload } from "@/components/ImageUpload";
import { HUBS } from "@/lib/brand";
import { Loader } from "@/components/Loader";

const EMPTY = { title: "", city: "", body: "", image_path: null };

export default function AdminNews() {
  const { t } = useApp();
  const [news, setNews] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/news").then(({ data }) => setNews(data.news)).catch(() => setNews([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (n) => { setForm({ ...EMPTY, ...n }); setEditing(n.id); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/news", form);
      else await api.put(`/admin/news/${editing}`, form);
      toast.success(t("saved")); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete post?")) return; await api.delete(`/admin/news/${id}`); toast.success(t("delete")); load(); };

  const inputCls = "cu-input w-full px-3 py-2.5 text-sm";

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-cu text-3xl">{t("news")}</h1>
        <button onClick={openNew} data-testid="admin-create-news-button" className="btn-gold px-5 py-2.5 text-xs flex items-center gap-2"><Plus size={15} /> {t("create_news")}</button>
      </div>

      {news === null ? <Loader /> : news.length === 0 ? <p className="text-sm text-[color:var(--cu-muted)] py-10 text-center">{t("no_news")}</p> : (
        <div className="space-y-3">
          {news.map((n) => (
            <div key={n.id} className="cu-card p-4 flex items-center gap-4" data-testid={`admin-news-${n.id}`}>
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-[rgba(212,175,55,0.06)]">
                {n.image_path && <FileImage path={n.image_path} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif-cu text-[color:var(--cu-text)] truncate">{n.title}</div>
                <div className="eyebrow mt-1 truncate">{n.city}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid={`news-edit-${n.id}`} onClick={() => openEdit(n)} className="w-9 h-9 flex items-center justify-center rounded-lg gold-border hover:bg-[rgba(212,175,55,0.08)] text-[color:var(--cu-gold-light)]"><Pencil size={15} /></button>
                <button data-testid={`news-delete-${n.id}`} onClick={() => del(n.id)} className="w-9 h-9 flex items-center justify-center rounded-lg gold-border hover:bg-[rgba(212,175,55,0.08)]"><Trash2 size={15} className="text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[color:var(--cu-charcoal)] border-[color:var(--cu-border)] text-[color:var(--cu-text)] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader><DialogTitle className="font-serif-cu text-xl">{editing === "new" ? t("create_news") : t("edit")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="eyebrow block mb-1.5">{t("title")}</label><input data-testid="news-form-title" value={form.title} onChange={set("title")} className={inputCls} /></div>
            <div><label className="eyebrow block mb-1.5">{t("city")}</label>
              <select value={form.city} onChange={set("city")} className={inputCls}>
                <option value="" className="bg-[color:var(--cu-charcoal)]">—</option>
                {HUBS.map((h) => <option key={h} className="bg-[color:var(--cu-charcoal)]">{h}</option>)}
              </select>
            </div>
            <div><label className="eyebrow block mb-1.5">{t("description")}</label><textarea data-testid="news-form-body" value={form.body} onChange={set("body")} rows={5} className={`${inputCls} resize-none`} /></div>
            <div><label className="eyebrow block mb-1.5">{t("image")}</label>
              <ImageUpload value={form.image_path} onChange={(path) => setForm((f) => ({ ...f, image_path: path }))} testId="news-image-upload" />
            </div>
            <button onClick={save} disabled={saving} data-testid="news-form-save" className="btn-gold w-full py-3 text-xs mt-2">{t("save")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
